import VersusPage, {
  type VersusComparisonRow,
  type VersusFaq,
  type VersusStat,
  type VersusWorkflow,
} from "@/components/public/versus-page"
import type { SeoMeta } from "@/types"

const STATS: VersusStat[] = [
  {
    value: "\u00A3300+",
    label: "per bespoke video",
    detail: "Typical UK property videographer rate for a single edited property video.",
  },
  {
    value: "3\u20135",
    label: "days turnaround",
    detail: "Shoot, edit, revisions. Meanwhile the listing is already live on Rightmove.",
  },
  {
    value: "\u00A3499",
    label: "per month, unlimited",
    detail: "QuiteRight: every listing gets a video ad \u2014 no booking, no shoot days, no waiting.",
  },
  {
    value: "60s",
    label: "per video",
    detail: "Same day the listing goes live, the ad is on Reels, TikTok and Facebook.",
  },
]

const WORKFLOW: VersusWorkflow = {
  competitorSteps: [
    "Request a quote",
    "Coordinate access with the vendor",
    "Shoot day (2\u20133 hours on site)",
    "Wait for first edit (usually 2\u20135 days)",
    "Review and request revisions",
    "Approve and receive final file",
    "Upload to every social channel manually",
  ],
  competitorTotal: "A week, per listing",
  quiterightSteps: [
    "Paste the listing URL",
    "QuiteRight builds the video and captions",
    "Publish to Instagram, Facebook, TikTok and YouTube",
  ],
  quiterightTotal: "60 seconds, per listing",
}

const COMPARISON: VersusComparisonRow[] = [
  { label: "Turnaround", competitor: "3\u20135 days after shoot", quiteright: "Minutes", highlight: true },
  { label: "Listings covered", competitor: "Flagship only (budget limited)", quiteright: "Every single listing", highlight: true },
  { label: "Cost per video", competitor: "\u00A3300+ typical", quiteright: "Effectively \u00A316 on \u00A3499/mo plan", highlight: true },
  { label: "Scheduling", competitor: "Vendor access required", quiteright: "Works from portal photos" },
  { label: "Social formats", competitor: "Usually 1\u20132 cuts", quiteright: "9:16, 1:1 and 16:9 in one render", highlight: true },
  { label: "Revisions", competitor: "Extra cost, extra days", quiteright: "Instant re-render, new template or voice" },
  { label: "Branding consistency", competitor: "Depends on editor", quiteright: "Locked template engine" },
  { label: "Publishing", competitor: "You upload manually", quiteright: "Auto-publish to 4 platforms" },
  { label: "Best use case", competitor: "\u00A32m+ flagship with cinematic walkthrough", quiteright: "Every listing, every week" },
]

const FAQ: VersusFaq[] = [
  {
    q: "Doesn't a real videographer make better video?",
    a: "For a flagship \u00A32m-plus listing with cinematic walk-throughs and drone work, yes \u2014 that's still their job. But you can only afford a videographer on a handful of listings. QuiteRight covers every other instruction with a professional, on-brand social video ad \u2014 the kind that actually gets seen in feed.",
  },
  {
    q: "Can I use both?",
    a: "Absolutely. The pattern most top agencies adopt: videographer for flagship sales + full-bodied brand films, QuiteRight for every individual listing's social ad campaign. One big video a year can't compete in feed with 100 listing-specific ads.",
  },
  {
    q: "What about the shoot day \u2014 don't buyers want walk-through footage?",
    a: "Serious buyers do \u2014 but they see the walk-through on the listing page, not on TikTok. The purpose of a social video ad is different: it stops a thumb scrolling, tells the story in 15\u201330 seconds, and pushes the viewer to the listing. That's what QuiteRight is built for.",
  },
  {
    q: "How does QuiteRight video quality compare?",
    a: "QuiteRight renders are broadcast-quality: AI voiceover, timed captions, proper motion, consistent branding, Ken Burns on stills, licensed music. Not cinematic drone footage \u2014 but exactly the format that performs in Reels, TikTok, and Facebook feed.",
  },
  {
    q: "What does the \u00A3499/month plan actually cover?",
    a: "Fully managed, unlimited video production and scheduled publishing across Instagram, Facebook, TikTok and YouTube. Compare that to \u00A3300+ per bespoke video, which usually only gets you one listing covered.",
  },
]

export default function VsVideographer({ meta }: { meta: SeoMeta }) {
  return (
    <VersusPage
      meta={meta}
      competitorName="hiring a videographer"
      competitorShortName="a videographer"
      breadcrumbName="QuiteRight vs Hiring a Videographer"
      heroHeadline="A videographer for the flagship. QuiteRight for every other listing"
      heroSubhead="A property videographer delivers beautiful cinematic films \u2014 at \u00A3300+ per listing, days to turn around. QuiteRight delivers a social-first video ad for every listing, in 60 seconds, for a flat monthly fee."
      statsHeadline="Scale from one listing to your whole book"
      statsSubhead="You can't afford a videographer for every listing \u2014 and you don't need one. You need a social video ad on every property, consistently, every week."
      stats={STATS}
      workflowHeadline="A week of coordination vs a minute of clicks"
      workflow={WORKFLOW}
      comparison={COMPARISON}
      faq={FAQ}
      ctaHeadline="Hire a videographer for flagships. Automate the rest"
      ctaBody="Keep the videographer for the \u00A32m instruction. Use QuiteRight to make sure every other listing also has a social video ad."
      relatedKey="vs/videographer"
    />
  )
}
